import {
  SubscriptionDetails,
  SubscriptionLog,
  CreateSubscriptionPayload,
  UpdateSubscriptionPayload,
  LeadDetails
} from "../../types"
import type { RateflowCard, FormattedRateflowCard } from "../../types/rateflow"

/**
 * Normalizes US phone numbers by stripping leading "1" country code
 */
const normalizePhone = (phone: string): string => {
  if (!phone) return ""
  const digits = phone.replace(/\D/g, "")
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1)
  }
  return digits
}

export const getStatusID = (
  data: SubscriptionDetails | SubscriptionLog
): string => {
  return data.deleted === "1"
    ? "delete"
    : data.active === "1"
    ? "active"
    : "disable"
}

/**
 * Formats a RateflowCard by converting the rate number to a string with proper decimals
 */
export const formatRateflowCard = (
  card: RateflowCard
): FormattedRateflowCard => {
  const { rate, ...rest } = card
  return {
    ...rest,
    rate: rate.toFixed(3) // Format rate as string with 3 decimal places
  }
}

/**
 * Converts SubscriptionDetails to CreateSubscriptionPayload or UpdateSubscriptionPayload
 * Can also accept leadDetails to populate form when creating from a lead
 */
export const convertSubscriptionDetailsToPayload = (
  details?: SubscriptionDetails,
  userId?: string,
  leadDetails?: LeadDetails,
  isCreateMode = false
): CreateSubscriptionPayload | UpdateSubscriptionPayload => {
  // When creating from a lead, use leadDetails
  const source = details || ({} as SubscriptionDetails)
  const lead = leadDetails || source.quote_info

  const payload: CreateSubscriptionPayload = {
    active: source.active || "1",
    deleted: source.deleted || "0",
    email: source.email || "",
    first_name: source.first_name || leadDetails?.first_name || "",
    last_name: source.last_name || leadDetails?.last_name || "",
    phone: normalizePhone(source.phone || ""),
    loid: source.loid || userId || "",

    // quote_info fields - use leadDetails if available, otherwise subscription details
    loan_type: (lead?.loan_type || "") as string,
    loan_term: String(lead?.loan_term || ""),
    arm_term: String(lead?.arm_term || ""),
    loan_purpose: (lead?.loan_purpose || "") as string,
    loan_amount: String(lead?.loan_amount || ""),
    credit_score: lead?.credit_score,
    property_type: (lead?.property_type || "") as string,
    residency_type: (lead?.residency_type || "") as string,
    list_price: String(lead?.list_price || ""),
    cash_out: lead?.cash_out ? String(lead.cash_out) : undefined,
    first_time_homebuyer:
      source.first_time_homebuyer ?? lead?.first_time_homebuyer ?? false,

    // location fields
    city: lead?.location?.city || "",
    county: lead?.location?.county || "",
    state: lead?.location?.state || "",
    street_address:
      lead?.location?.street_address || lead?.location?.street || "",
    zipcode: lead?.location?.zipcode || "",

    // admin_fields
    taxes: lead?.admin_fields?.taxes || 0,
    insurance: lead?.admin_fields?.insurance || 0,
    seller_credit: lead?.admin_fields?.seller_credit || 0,

    // extra flags
    waive_escrows: source.waive_escrows ?? false,

    // optional fields
    lead_id: source.lead_id,
    va_funding_fee_type: lead?.va_funding_fee_type,

    // custom fee
    custom_fee_label: lead?.admin_fields?.custom_fee_label ?? "",
    custom_fee_amount: lead?.admin_fields?.custom_fee_amount ?? undefined
  }

  if (!isCreateMode && details?.id) {
    return {
      ...payload,
      id: details.id
    } as UpdateSubscriptionPayload
  }

  return payload
}

/**
 * Converts CreateSubscriptionPayload back to partial SubscriptionDetails
 * Note: This creates a partial object since some fields are only available from the server
 */
export const convertPayloadToSubscriptionDetails = (
  payload: CreateSubscriptionPayload | UpdateSubscriptionPayload
): Partial<SubscriptionDetails> => {
  const details: Partial<SubscriptionDetails> = {
    active: payload.active,
    deleted: payload.deleted,
    email: payload.email,
    first_name: payload.first_name,
    last_name: payload.last_name,
    phone: payload.phone,
    loid: payload.loid,
    waive_escrows: payload.waive_escrows,
    first_time_homebuyer: payload.first_time_homebuyer,
    lead_id: payload.lead_id,
    quote_info: {
      loan_type: payload.loan_type,
      loan_term: payload.loan_term,
      arm_term: payload.arm_term,
      loan_purpose: payload.loan_purpose,
      loan_amount: payload.loan_amount,
      credit_score: payload.credit_score,
      property_type: payload.property_type,
      residency_type: payload.residency_type,
      list_price: payload.list_price,
      cash_out: payload.cash_out,
      va_funding_fee_type: payload.va_funding_fee_type,
      location: {
        city: payload.city,
        county: payload.county,
        state: payload.state,
        street_address: payload.street_address,
        zipcode: payload.zipcode
      },
      admin_fields: {
        taxes: payload.taxes,
        insurance: payload.insurance,
        seller_credit: payload.seller_credit,
        custom_fee_label: payload.custom_fee_label,
        custom_fee_amount: payload.custom_fee_amount
      }
    } as any
  }

  if ("id" in payload && payload.id) {
    details.id = payload.id
  }

  return details
}
